<template>
  <SfDropdown
    trigger-size="small"
    :is-small="true"
    :dropdown-id="dropdownId"
    :is-open="isScopedDropdownOpen"
    variant="primary-inverted"
    @dropdown-trigger-clicked="handleToggleDropdown"
    @dropdown-closed="handleCloseDropdown"
  >
    <template #trigger>
      {{ dropdownLabel }}
    </template>

    <template #menu>
      <div
        class="sf-pad-2 remap-dropdown__container-menu"
        data-testid="remap-dropdown-content"
      >
        <div
          class="
            sf-below-1 sf-c-text sf-font-xs-heading sf-font-medium
            remap-dropdown__title
          "
        >
          {{ title }}
        </div>
        <div class="sf-below-1 sf-font-xs sf-c-text-darker">
          {{ subtitle }}
        </div>

        <div v-if="isScopedDropdownOpen" class="sf-mapping-list">
          <div class="remap-dropdown__search-row sf-below-2">
            <remap-dropdown-search />

            <div class="sf-btn-bar">
              <component
                :is="showAiOnlyComponent"
                v-if="props.items.length"
                variant="primary"
                data-testid="show-ai-only-button"
                @click="setShow('ai')"
              >
                <i class="fa fa-wand-magic-sparkles" />
                {{ showAiOnlyText }}
              </component>

              <component
                :is="showExternalOnlyComponent"
                v-if="displayExternalButton"
                variant="primary"
                data-testid="show-external-only-button"
                @click="setShow('external')"
              >
                {{ showExternalOnlyText }}
              </component>

              <component
                :is="showAllComponent"
                variant="primary"
                data-testid="show-all-button"
                @click="setShow('all')"
              >
                {{ showAllText }}
              </component>
            </div>
          </div>
          <div
            class="
              sf-font-s sf-c-text-dark sf-font-medium sf-below-1
              remap-dropdown__subheader
            "
          >
            {{ chartOfAccountsText }}
          </div>
          <account-group-list
            v-scroll-into-view="{ behavior: 'smooth', block: 'nearest' }"
            @item-clicked="handleTreeItemClick"
          >
          </account-group-list>
        </div>
      </div>
    </template>
  </SfDropdown>
</template>
<script setup lang="ts">
import { dropdowns } from '@app/shared/types/dropdown'
import { AccountId, AccountRange } from '../shared-types'
import { computed } from 'vue'

import {
  SfButtonInverted,
  SfButton,
} from '@app/shared/vue/components/sf-button'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import AccountGroupList from './account-group-list.vue'
import RemapDropdownSearch from './remap-dropdown-search.vue'
import { useCommon } from '@app/application/account-mapping/store/common'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { useGroupList } from '@app/application/account-mapping/store/group-list'
import { useSearch } from '../store/search'
import scrollIntoView from '@app/shared/vue/directives/scrollIntoView'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'RemapDropdown',
})

interface Props {
  accountId: AccountId
  accountRange: AccountRange
  items: Array<AccountRange>
  externallyMappedNumber?: string
}

const props = withDefaults(defineProps<Props>(), {
  items: function () {
    return []
  },
  externallyMappedNumber: '',
})

const vScrollIntoView = scrollIntoView

const commonStore = useCommon()
const searchStore = useSearch()
const dropdownStore = useDropdown()
const groupListStore = useGroupList()

const dropdownId = dropdowns.ACCOUNT_MAPPING_TABLE_REMAP

const dropdownLabel = window.I18n.t('account_mapping_list.dropdown.remap_label')
const title = window.I18n.t('account_mapping_list.remap.title')
const subtitle = window.I18n.t('account_mapping_list.remap.subtitle')
const showAiOnlyText = window.I18n.t(
  'account_mapping_list.remap.ai_only_toggle'
)
const showExternalOnlyText = window.I18n.t(
  'account_mapping_list.remap.external_only_toggle'
)
const showAllText = window.I18n.t('account_mapping_list.remap.show_all_toggle')

const chartOfAccountsText = window.I18n.t(
  'account_mapping_list.silverfin_chart_of_accounts'
)

const isScopedDropdownOpen = computed(
  () =>
    dropdownStore.currentDropdownId === dropdownId &&
    groupListStore.focusedDropdown === props.accountId
)
const showAiOnlyComponent = computed(() =>
  searchStore.remapDropdownShowGroup === 'ai' ? SfButton : SfButtonInverted
)

const showExternalOnlyComponent = computed(() =>
  searchStore.remapDropdownShowGroup === 'external'
    ? SfButton
    : SfButtonInverted
)

const showAllComponent = computed(() =>
  searchStore.remapDropdownShowGroup === 'all' ? SfButton : SfButtonInverted
)

const displayExternalButton = computed(() => {
  if (!props.externallyMappedNumber) {
    return false
  }

  return groupListStore.getExternallyMappedGroupIds.length > 0
})

function handleCloseDropdown() {
  groupListStore.persistDropdown('')
  dropdownStore.closeDropdown()
}

function handleToggleDropdown() {
  if (groupListStore.focusedDropdown === props.accountId) {
    groupListStore.persistDropdown('')
  } else {
    dropdownStore.closeDropdown() // close dropdown explicitly if we're clicking on another Remap button, otherwise the toggle below won't work
    groupListStore.persistDropdown(props.accountId)

    const aiGroupIds = dropdownAIGroupIds.value
    const externalGroupIds = groupListStore.getExternallyMappedGroupIds

    searchStore.setRemapDropdownAIFitleredGroupIds(aiGroupIds)
    searchStore.setRemapDropdownExternalFitleredGroupIds(externalGroupIds)

    if (externalGroupIds.length) {
      searchStore.setRemapDropdownShowGroup('external')
    } else if (aiGroupIds.length) {
      searchStore.setRemapDropdownShowGroup('ai')
    } else {
      searchStore.setRemapDropdownShowGroup('all')
    }
    commonStore.clearSearch()
    commonStore.focusRemapSearch()
  }

  dropdownStore.toggleDropdown(dropdownId)
}

const dropdownAIGroupIds = computed(() =>
  props.items.map((value) => commonStore.getAccountRangePerGroup[value])
)

function handleTreeItemClick({
  accountRange,
}: {
  id?: string
  accountRange: string
}) {
  if (accountRange === props.accountRange) {
    groupListStore.unmap({
      accountId: props.accountId,
      accountRange,
    })
  } else {
    groupListStore.remap({
      accountId: props.accountId,
      prevAccountRange: props.accountRange,
      accountRange,
    })
  }

  handleCloseDropdown()
}

function setShow(value: 'ai' | 'external' | 'all') {
  searchStore.setRemapDropdownShowGroup(value)
}
</script>
<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.remap-dropdown {
  &__container-menu {
    min-width: 600px;
  }

  &__search-row {
    display: flex;
    gap: space(1);
  }

  &__title {
    line-height: 1.5;
  }

  &__subtitle {
    line-height: 1.181;
  }

  &__subheader {
    line-height: 1.23;
  }
}
</style>
